import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HiOutlineExclamationCircle, HiOutlineSwitchHorizontal, HiOutlineX } from 'react-icons/hi';
import SearchBar from '../../components/table/SearchBar';
import DataTable from '../../components/table/DataTable';
import DateFilter from '../../components/table/DateFilter';
import { supabase } from '../../utils/supabase';
import { isReservedFieldName } from '../../utils/config';
import { generateRecordCode } from '../../utils/generateId';
import { formatNaira, getCurrencyFields, isCurrencyField, parseCurrencyValue } from '../../utils/currency';
import SuccessModal from '../../components/feedback/SuccessModal';
import { useSchool } from '../../context/SchoolContext';

function ViewAll({ section, selectedTerm }) {
  const { sections } = useSchool();
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingRecord, setEditingRecord] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [successModal, setSuccessModal] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedRecordIds, setSelectedRecordIds] = useState([]);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moving, setMoving] = useState(false);

  const fields = useMemo(
    () => (section?.fields || []).filter(field => !isReservedFieldName(field)),
    [section]
  );

  const fetchRecords = useCallback(async () => {
    if (!section?.id || !selectedTerm?.id) {
      setRecords([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data, error: recordsError } = await supabase
        .from('records')
        .select('id, section_id, term_id, record_code, record_id, data, created_at')
        .eq('section_id', section.id)
        .eq('term_id', selectedTerm.id)
        .order('created_at', { ascending: false });

      if (recordsError) throw recordsError;

      setRecords(data || []);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Could not load records.');
    } finally {
      setLoading(false);
    }
  }, [section?.id, selectedTerm?.id]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    setSelectedRecordIds([]);
    setMoveModalOpen(false);
  }, [section?.id, selectedTerm?.id]);

  const visibleRecords = useMemo(() => {
    const query = search.trim().toLowerCase();

    const filtered = records.filter(record => {
      if (!isWithinDateRange(record.created_at, fromDate, toDate)) {
        return false;
      }

      if (!query) {
        return true;
      }

      const systemValues = [
        record.record_code,
        record.record_id,
        record.created_at,
      ];
      const fieldValues = fields.map(field => record.data?.[field]);

      return [...systemValues, ...fieldValues]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(query));
    });

    return filtered;
  }, [records, search, fields, fromDate, toDate]);

  const selectedRecords = useMemo(
    () => records.filter(record => selectedRecordIds.includes(record.id)),
    [records, selectedRecordIds]
  );

  const destinationSections = useMemo(
    () => (sections || []).filter(item => item.id !== section?.id),
    [sections, section?.id]
  );

  const toggleRecordSelection = useCallback((recordId) => {
    setSelectedRecordIds(current => (
      current.includes(recordId)
        ? current.filter(id => id !== recordId)
        : [...current, recordId]
    ));
  }, []);

  const toggleVisibleSelection = useCallback(() => {
    const visibleIds = visibleRecords.map(record => record.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedRecordIds.includes(id));

    setSelectedRecordIds(current => (
      allVisibleSelected
        ? current.filter(id => !visibleIds.includes(id))
        : Array.from(new Set([...current, ...visibleIds]))
    ));
  }, [selectedRecordIds, visibleRecords]);

  const moveSelectedRecords = async (destinationSection, fieldMapping) => {
    const recordsToMove = records.filter(record => selectedRecordIds.includes(record.id));

    if (recordsToMove.length === 0 || !destinationSection?.id || !selectedTerm?.id) {
      return;
    }

    setMoving(true);
    setError('');
    const movedIds = [];

    try {
      const { data: destinationRecords, error: destinationError } = await supabase
        .from('records')
        .select('record_code, record_id')
        .eq('section_id', destinationSection.id)
        .eq('term_id', selectedTerm.id);

      if (destinationError) throw destinationError;

      const existingCodes = (destinationRecords || [])
        .map(record => record.record_code || record.record_id)
        .filter(Boolean);

      for (const record of recordsToMove) {
        const nextRecordCode = generateRecordCode(destinationSection.initials, existingCodes);
        existingCodes.push(nextRecordCode);

        const { error: updateError } = await supabase
          .from('records')
          .update({
            section_id: destinationSection.id,
            record_code: nextRecordCode,
            record_id: nextRecordCode,
            data: applyFieldMapping(record.data, fieldMapping),
          })
          .eq('id', record.id);

        if (updateError) throw updateError;
        movedIds.push(record.id);
      }

      setRecords(current => current.filter(record => !movedIds.includes(record.id)));
      setSelectedRecordIds([]);
      setMoveModalOpen(false);
      setSuccessModal({
        title: 'Records Moved',
        message: `${movedIds.length} record${movedIds.length === 1 ? '' : 's'} moved to ${destinationSection.name}.`,
      });
    } catch (err) {
      console.error(err);
      if (movedIds.length > 0) {
        await fetchRecords();
        setSelectedRecordIds(current => current.filter(id => !movedIds.includes(id)));
      }
      setError(
        movedIds.length > 0
          ? `${movedIds.length} record${movedIds.length === 1 ? '' : 's'} moved, but the rest could not be moved: ${err?.message || 'Unknown error'}`
          : err?.message || 'Could not move selected records.'
      );
    } finally {
      setMoving(false);
    }
  };

  return (
    <div className="space-y-5">
      <SuccessModal
        title={successModal?.title}
        message={successModal?.message}
        onClose={() => setSuccessModal(null)}
      />

      <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-start 2xl:justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-neutral-100">All Records</h2>
          <p className="text-sm text-gray-500 dark:text-neutral-400">
            {visibleRecords.length} of {records.length} saved for {section.name}
          </p>
        </div>
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <SearchBar value={search} onChange={setSearch} />
        </div>
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
        <div className="grid grid-cols-1 gap-4 2xl:grid-cols-[1fr_auto] 2xl:items-end">
          <DateFilter
            fromDate={fromDate}
            toDate={toDate}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />

          {selectedRecords.length > 0 && (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              <span className="rounded-full bg-blue-50 px-3 py-1.5 text-xs font-bold text-blue-700 dark:bg-blue-950/40 dark:text-blue-200">
                {selectedRecords.length} selected
              </span>
              <button
                type="button"
                onClick={() => setMoveModalOpen(true)}
                disabled={destinationSections.length === 0}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 dark:disabled:bg-neutral-800"
              >
                <HiOutlineSwitchHorizontal className="text-lg" />
                Move
              </button>
              <button
                type="button"
                onClick={() => setSelectedRecordIds([])}
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-bold text-gray-600 transition hover:bg-gray-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-600 dark:border-red-800 dark:bg-neutral-900 dark:text-red-300">
          Could not load records: {error}
        </div>
      )}

      {loading ? (
        <div className="rounded-xl border border-gray-100 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
          <div className="space-y-3">
            {[0, 1, 2, 3].map(item => (
              <div key={item} className="h-12 animate-pulse rounded-lg bg-gray-100 dark:bg-neutral-800" />
            ))}
          </div>
          <p className="mt-4 text-center text-sm font-medium text-gray-400 dark:text-neutral-500">Loading records...</p>
        </div>
      ) : (
        <DataTable
          fields={fields}
          records={visibleRecords}
          selectedIds={selectedRecordIds}
          onToggleSelect={toggleRecordSelection}
          onToggleSelectAll={toggleVisibleSelection}
          onEdit={setEditingRecord}
          onDelete={setRecordToDelete}
        />
      )}

      {moveModalOpen && (
        <MoveRecordsModal
          records={selectedRecords}
          sourceSection={section}
          sourceFields={fields}
          destinationSections={destinationSections}
          moving={moving}
          onClose={() => setMoveModalOpen(false)}
          onMove={moveSelectedRecords}
        />
      )}

      {editingRecord && (
        <EditRecordModal
          fields={fields}
          record={editingRecord}
          saving={saving}
          onClose={() => setEditingRecord(null)}
          onSave={async (nextData) => {
            setSaving(true);
            setError('');
            const normalizedData = normalizeEditableValues(nextData, fields);

            try {
              const { data, error: updateError } = await supabase
                .from('records')
                .update({ data: normalizedData })
                .eq('id', editingRecord.id)
                .select('id, section_id, term_id, record_code, record_id, data, created_at')
                .single();

              if (updateError) throw updateError;

              setRecords(current => current.map(record =>
                record.id === data.id ? data : record
              ));
              setEditingRecord(null);
              setSuccessModal({
                title: 'Record Updated',
                message: `${data.record_code || data.record_id} was updated successfully.`,
              });
            } catch (err) {
              console.error(err);
              setError(err?.message || 'Could not update record.');
            } finally {
              setSaving(false);
            }
          }}
        />
      )}

      {recordToDelete && (
        <ConfirmDeleteModal
          record={recordToDelete}
          saving={saving}
          onCancel={() => setRecordToDelete(null)}
          onConfirm={async () => {
            setSaving(true);
            setError('');

            try {
              const { error: deleteError } = await supabase
                .from('records')
                .delete()
                .eq('id', recordToDelete.id);

              if (deleteError) throw deleteError;

              const deletedRecordCode = recordToDelete.record_code || recordToDelete.record_id;
              setRecords(current => current.filter(record => record.id !== recordToDelete.id));
              setRecordToDelete(null);
              setSelectedRecordIds(current => current.filter(id => id !== recordToDelete.id));
              setSuccessModal({
                title: 'Record Deleted',
                message: `${deletedRecordCode} was removed successfully.`,
              });
            } catch (err) {
              console.error(err);
              setError(err?.message || 'Could not delete record.');
            } finally {
              setSaving(false);
            }
          }}
        />
      )}
    </div>
  );
}

function MoveRecordsModal({
  records,
  sourceSection,
  sourceFields,
  destinationSections,
  moving,
  onClose,
  onMove,
}) {
  const [destinationId, setDestinationId] = useState(destinationSections[0]?.id || '');
  const destinationSection = destinationSections.find(section => section.id === destinationId);
  const destinationFields = useMemo(
    () => (destinationSection?.fields || []).filter(field => !isReservedFieldName(field)),
    [destinationSection]
  );
  const sourceCurrencyFields = useMemo(() => getCurrencyFields(sourceFields), [sourceFields]);
  const destinationCurrencyFields = useMemo(() => getCurrencyFields(destinationFields), [destinationFields]);
  const [fieldMapping, setFieldMapping] = useState({});

  useEffect(() => {
    const nextMapping = {};

    sourceFields.forEach(sourceField => {
      const exactMatch = destinationFields.find(destinationField => (
        normalizeFieldName(destinationField) === normalizeFieldName(sourceField)
      ));
      const currencyFallback = isCurrencyField(sourceField) && destinationCurrencyFields.length === 1
        ? destinationCurrencyFields[0]
        : '';

      nextMapping[sourceField] = exactMatch || currencyFallback;
    });

    setFieldMapping(nextMapping);
  }, [destinationFields, destinationCurrencyFields, sourceFields]);

  const currencyPreview = useMemo(() => (
    sourceCurrencyFields.map(field => ({
      field,
      destinationField: fieldMapping[field],
      total: records.reduce((sum, record) => sum + parseCurrencyValue(record.data?.[field]), 0),
    })).filter(item => item.total !== 0)
  ), [fieldMapping, records, sourceCurrencyFields]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-800/50 px-4 py-6">
      <div className="w-full max-w-2xl rounded-xl border border-gray-100 bg-white shadow-xl dark:border-neutral-800 dark:bg-neutral-900">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4 dark:border-neutral-800">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Move Records</p>
            <h3 className="mt-1 text-lg font-bold text-gray-900 dark:text-neutral-100">
              {records.length} from {sourceSection.name}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={moving}
            className="text-gray-400 transition hover:text-gray-700 disabled:opacity-50 dark:hover:text-neutral-200"
            aria-label="Close move records"
          >
            <HiOutlineX className="text-xl" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-5">
          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-gray-700 dark:text-neutral-200">
              Move to section
            </span>
            <select
              value={destinationId}
              onChange={(event) => setDestinationId(event.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 outline-none focus:ring-2 focus:ring-blue-500 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100"
            >
              {destinationSections.map(section => (
                <option key={section.id} value={section.id}>
                  {section.name}
                </option>
              ))}
            </select>
          </label>

          <div className="mt-5 rounded-xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
            <div className="flex gap-3">
              <HiOutlineExclamationCircle className="mt-0.5 shrink-0 text-lg" />
              <p>
                The record ID will change to the destination section format. The original form data stays saved, and mapped fields are copied into the destination fields so they show in the table.
              </p>
            </div>
          </div>

          {sourceFields.length > 0 && (
            <div className="mt-5">
              <h4 className="text-sm font-bold text-gray-900 dark:text-neutral-100">Field mapping</h4>
              <p className="mt-1 text-sm text-gray-500 dark:text-neutral-400">
                Match fields when the destination section uses a different name or capitalisation.
              </p>

              <div className="mt-3 space-y-3">
                {sourceFields.map(field => (
                  <label key={field} className="grid grid-cols-1 gap-2 rounded-xl border border-gray-100 p-3 dark:border-neutral-800 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
                    <span className="text-sm font-semibold text-gray-700 dark:text-neutral-200">
                      {field}
                      {isCurrencyField(field) && (
                        <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200">
                          money
                        </span>
                      )}
                    </span>
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-400">to</span>
                    <select
                      value={fieldMapping[field] || ''}
                      onChange={(event) => setFieldMapping(current => ({
                        ...current,
                        [field]: event.target.value,
                      }))}
                      className="rounded-xl border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-blue-500 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100"
                    >
                      <option value="">Do not map</option>
                      {destinationFields.map(destinationField => (
                        <option key={destinationField} value={destinationField}>
                          {destinationField}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            </div>
          )}

          {currencyPreview.length > 0 && (
            <div className="mt-5 rounded-xl border border-gray-100 bg-gray-50 p-4 dark:border-neutral-800 dark:bg-neutral-950">
              <h4 className="text-sm font-bold text-gray-900 dark:text-neutral-100">Totals preview</h4>
              <div className="mt-3 space-y-2">
                {currencyPreview.map(item => (
                  <div key={item.field} className="flex flex-col gap-1 text-sm sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-gray-600 dark:text-neutral-300">
                      {item.field}{item.destinationField ? ` to ${item.destinationField}` : ' not mapped'}
                    </span>
                    <span className="font-bold text-gray-900 dark:text-neutral-100">{formatNaira(item.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-100 px-5 py-4 dark:border-neutral-800">
          <button
            type="button"
            onClick={onClose}
            disabled={moving}
            className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onMove(destinationSection, fieldMapping)}
            disabled={moving || !destinationSection}
            className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400"
          >
            {moving ? 'Moving...' : 'Move Records'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditRecordModal({ fields, record, saving, onClose, onSave }) {
  const [values, setValues] = useState(() => buildEditableValues(fields, record.data));

  useEffect(() => {
    setValues(buildEditableValues(fields, record.data));
  }, [fields, record]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-800/50 px-4 py-6">
      <div className="w-full max-w-2xl rounded-xl border border-gray-100 bg-white shadow-xl dark:border-neutral-800 dark:bg-neutral-900">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4 dark:border-neutral-800">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Edit Record</p>
            <h3 className="mt-1 text-lg font-bold text-gray-900 dark:text-neutral-100">
              {record.record_code || record.record_id}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 transition hover:text-gray-700 dark:hover:text-neutral-200"
            aria-label="Close edit record"
          >
            <HiOutlineX className="text-xl" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ReadOnlyField label="Record ID" value={record.record_code || record.record_id} />
            <ReadOnlyField label="Created" value={formatDate(record.created_at)} />
          </div>

          <div className="mt-5 space-y-4">
            {fields.map(field => (
              <label key={field} className="block">
                <span className="mb-1 block text-sm font-semibold text-gray-700 dark:text-neutral-200">
                  {field}
                </span>
                <div className="relative">
                  {isCurrencyField(field) && (
                    <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-400">
                      {'\u20A6'}
                    </span>
                  )}
                  <input
                    type={isCurrencyField(field) ? 'number' : 'text'}
                    inputMode={isCurrencyField(field) ? 'decimal' : undefined}
                    min={isCurrencyField(field) ? '0' : undefined}
                    step={isCurrencyField(field) ? '0.01' : undefined}
                    value={values[field] || ''}
                    onChange={(event) => setValues(current => ({
                      ...current,
                      [field]: event.target.value,
                    }))}
                    className={`w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 outline-none focus:ring-2 focus:ring-blue-500 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100 ${
                      isCurrencyField(field) ? 'pl-10' : ''
                    }`}
                    placeholder={`Enter ${field.toLowerCase()}`}
                  />
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-100 px-5 py-4 dark:border-neutral-800">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(values)}
            disabled={saving}
            className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({ record, saving, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-800/50 px-4 py-6">
      <div className="w-full max-w-md rounded-xl border border-gray-100 bg-white p-5 shadow-xl dark:border-neutral-800 dark:bg-neutral-900">
        <h3 className="text-lg font-bold text-gray-900 dark:text-neutral-100">Delete record?</h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-neutral-400">
          This will permanently delete {record.record_code || record.record_id}. This action cannot be undone.
        </p>

        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:bg-gray-200 disabled:text-gray-400"
          >
            {saving ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div>
      <p className="mb-1 text-xs font-bold uppercase tracking-wider text-gray-400">{label}</p>
      <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-500 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-300">
        {value || '-'}
      </div>
    </div>
  );
}

function buildEditableValues(fields, data = {}) {
  return fields.reduce((values, field) => {
    values[field] = isCurrencyField(field)
      ? String(data?.[field] || '').replace(/[^\d.-]/g, '')
      : data?.[field] || '';
    return values;
  }, {});
}

function applyFieldMapping(data = {}, mapping = {}) {
  const nextData = { ...data };

  Object.entries(mapping).forEach(([sourceField, destinationField]) => {
    if (!destinationField || sourceField === destinationField) {
      return;
    }

    const sourceValue = data?.[sourceField];
    const destinationValue = nextData?.[destinationField];

    if (sourceValue === undefined || sourceValue === null || sourceValue === '') {
      return;
    }

    if (destinationValue === undefined || destinationValue === null || destinationValue === '') {
      nextData[destinationField] = isCurrencyField(sourceField) || isCurrencyField(destinationField)
        ? String(sourceValue).replace(/[^\d.-]/g, '')
        : sourceValue;
    }
  });

  return nextData;
}

function normalizeFieldName(fieldName = '') {
  return String(fieldName).trim().toLowerCase().replace(/[_-]+/g, ' ');
}

function formatDate(value) {
  if (!value) return '-';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function isWithinDateRange(value, fromDate, toDate) {
  if (!value) return true;

  const created = new Date(value);

  if (fromDate) {
    const from = new Date(`${fromDate}T00:00:00`);
    if (created < from) return false;
  }

  if (toDate) {
    const to = new Date(`${toDate}T23:59:59`);
    if (created > to) return false;
  }

  return true;
}

function normalizeEditableValues(values, fields) {
  return fields.reduce((nextValues, field) => {
    nextValues[field] = isCurrencyField(field)
      ? String(values[field] || '').replace(/[^\d.-]/g, '')
      : values[field];

    return nextValues;
  }, {});
}

export default ViewAll;
